import { CSSProperties, FC, ReactNode } from "react";
import { clsx } from "clsx";
import { ReactComponent as GroupGradient } from "../../../../../assets/icons/svg/group-gradient.svg";
import { formatNumberWithSuffix, roundToTwoDecimals } from "../../../../../common/Number";
import { getScoreGrade } from "../../../../../common/Grade";
import { NegotiationIntelligence } from "../../../../../types/DashboardTypes";
import classes from "./Banner.module.less";

export interface BannerMetric {
    label: string;
    value: ReactNode;
    /** Shown after the number, smaller: "/ year". */
    unit?: ReactNode;
    /** The number's color: the default blue, green for savings, red for money lost. */
    color?: "blue" | "green" | "red";
    /** A line under the number: what it is of, or how it moved. */
    detail?: ReactNode;
}

/** A count in the greeting sentence: the metrics' blue, or orange for what waits on the user. */
export const BannerCount: FC<{ color?: "blue" | "orange"; children: ReactNode }> = ({
    color = "blue",
    children,
}) => (
    <span className={clsx(classes.count, color === "orange" && classes["count--orange"])}>
        {children}
    </span>
);

interface BannerProps {
    name?: string;
    /** Replaces the savings sentence after the greeting. */
    message?: ReactNode;
    /** Headline numbers under the greeting, in place of the negotiation intelligence. */
    metrics?: BannerMetric[];
    /** The sum of the deals' target savings. */
    savings?: number;
    /** The sum of the deals' realized savings. */
    realized?: number;
    contracts?: number;
    year?: number | null;
    intelligence?: NegotiationIntelligence;
    style?: CSSProperties;
}

export const Banner: FC<BannerProps> = ({
    name = "",
    message,
    metrics,
    savings = 0,
    realized = 0,
    contracts = 0,
    year,
    intelligence,
    style,
}) => {
    const avgScoreGrade = intelligence ? getScoreGrade(intelligence.avgOrderScore) : null;
    const avgOptimizedGrade = intelligence ? getScoreGrade(intelligence.avgOptimizedScore) : null;
    const scoreDiff = intelligence
        ? roundToTwoDecimals(intelligence.avgOrderScore - intelligence.totalAvgScore)
        : 0;
    const diffPrefix = scoreDiff > 0 ? "above" : "below";

    return (
        <div className={classes.container} style={style}>
            <div className={classes.top}>
                <GroupGradient />
                <span>
                    <span className={classes.name}>Hi{name ? ` ${name}` : ""}</span>,{" "}
                    {message ?? (
                        <>
                            you have realized{" "}
                            <span className={classes.money}>
                                ${formatNumberWithSuffix(realized, 0)}
                            </span>{" "}
                            of the{" "}
                            <span className={classes.money}>
                                ${formatNumberWithSuffix(savings, 0)}
                            </span>{" "}
                            you could save across {contracts}{" "}
                            {contracts === 1 ? "analyzed deal" : "analyzed deals"}
                            {year ? ` renewing in ${year}` : ""}.
                        </>
                    )}
                </span>
            </div>
            {metrics && (
                <div className={classes.metrics}>
                    {metrics.map((metric) => (
                        <div className={classes.metricBox} key={metric.label}>
                            <div className={classes.metricLabel}>{metric.label}</div>
                            <div
                                className={clsx(
                                    classes.metricValue,
                                    metric.color === "green" && classes["metricValue--green"],
                                    metric.color === "red" && classes["metricValue--red"]
                                )}
                            >
                                {metric.value}
                                {metric.unit && (
                                    <span className={classes.metricUnit}>{metric.unit}</span>
                                )}
                            </div>
                            {metric.detail && (
                                <div className={classes.metricDerived}>{metric.detail}</div>
                            )}
                        </div>
                    ))}
                </div>
            )}
            {!metrics && intelligence && (
                <div className={classes.metrics}>
                    <div className={classes.metricBox}>
                        <div className={classes.metricLabel}>Avg contract score</div>
                        <div
                            className={classes.metricValue}
                            style={{ color: avgScoreGrade?.color }}
                        >
                            {roundToTwoDecimals(intelligence.avgOrderScore)}% (
                            {avgScoreGrade?.grade})
                        </div>
                        <div className={classes.metricDerived}>
                            {Math.abs(scoreDiff)}% {diffPrefix} average
                        </div>
                    </div>
                    <div className={classes.metricBox}>
                        <div className={classes.metricLabel}>Avg optimized contract score</div>
                        <div
                            className={classes.metricValue}
                            style={{ color: avgOptimizedGrade?.color }}
                        >
                            {roundToTwoDecimals(intelligence.avgOptimizedScore)}% (
                            {avgOptimizedGrade?.grade})
                        </div>
                    </div>
                    <div className={classes.metricBox}>
                        <div className={classes.metricLabel}>Analyzed spend</div>
                        <div className={classes.metricValue}>
                            ${formatNumberWithSuffix(intelligence.totalSpend)}
                        </div>
                    </div>
                    <div className={classes.metricBox}>
                        <div className={classes.metricLabel}>Number of deals</div>
                        <div className={classes.metricValue}>
                            {intelligence.orderCount + intelligence.rfxCount}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
